import type AppStore from './AppStore'
import { type Quarter, FeatureType, type Project } from './features'

interface ChartData {
  heat_demand__sum: number
  heat_demand_renovation__sum: number
  solar_potential__sum: number
  heat_co2_emission__sum: number
  power_co2_emission__sum: number
  heat_energy_cost__sum: number
  power_energy_cost__sum: number
  residents__sum: number
}

interface DemandChartData {
  bisko: number
  heat_demand__sum: number
  power_demand__sum: number
}

class DashboardStore {
  constructor(private readonly appStore: AppStore) {}

  get selectedQuarter(): Quarter | null {
    if (this.appStore.focusType === FeatureType.Quarter) {
      return this.appStore.featureFocus as Quarter
    }

    return null
  }

  set quarterDropdownValue(value: string | undefined) {
    if (value === undefined) this.appStore.featureFocus = null
    else
      this.appStore.featureFocus =
        this.appStore.selectedProject.quarters.get(value) ?? null
  }

  get quarterDropdownValue(): string | undefined {
    return this.selectedQuarter?.uuid
  }

  get selectedObject(): Quarter | Project {
    return this.selectedQuarter ?? this.appStore.selectedProject
  }

  get demandChartData(): DemandChartData[] | undefined {
    return this.selectedObject.buildings.aggregates.groupBy(
      { heat_demand: 'sum', power_demand: 'sum' },
      ['bisko']
    ) as DemandChartData[] | undefined
  }

  get chartData(): ChartData | undefined {
    return this.selectedObject.buildings.aggregates.aggregate({
      heat_demand: 'sum',
      heat_demand_renovation: 'sum',
      solar_potential: 'sum',
      heat_co2_emission: 'sum',
      power_co2_emission: 'sum',
      residents: 'sum',
      power_energy_cost: 'sum',
      heat_energy_cost: 'sum'
    }) as ChartData | undefined
  }

  get emissionsPerResident(): number | undefined {
    if (this.chartData === undefined) return
    if (this.chartData.residents__sum === 0) return NaN
    return (
      (this.chartData.heat_co2_emission__sum +
        this.chartData.power_co2_emission__sum) /
      this.chartData.residents__sum
    )
  }

  get costPerResident(): number | undefined {
    if (this.chartData === undefined) return
    if (this.chartData.residents__sum === 0) return NaN
    return (
      (this.chartData?.heat_energy_cost__sum +
        this.chartData?.power_energy_cost__sum) /
      this.chartData.residents__sum
    )
  }

  get energyPlantData(): Array<Record<string, number>> | undefined {
    return this.selectedObject.energyPlants.aggregates.groupBy(
      { output_power: 'sum', output_heat: 'sum' },
      ['plant_type']
    )
  }
}

export default DashboardStore
